import React from 'react';
import { of } from 'rxjs';
import { Button, PRIMARY, SECONDARY } from '@glu/buttons-react';
import { createUseStyles } from '@glu/theming';
import PropTypes from 'prop-types';
import LocaleContext from 'components/locale';

const useStyles = createUseStyles(
  ({ palette: { primary }, spacing: { unit }, typography: { headline2 } }) => ({
    animatingButton: {
      transitionTimingFunction: 'cubic-bezier(0, 0.25, 1)',
      position: 'relative',
      overflow: 'hidden',
      zIndex: '1',
      '&:after': {
        content: '""',
        position: 'absolute',
        bottom: '0',
        left: '0',
        width: '100%',
        height: '100%',
        borderRadius: '10rem',
        zIndex: '-2',
      },
      '&:before': {
        content: '""',
        position: 'absolute',
        bottom: '0',
        left: '0',
        width: '0%',
        height: '100%',
        backgroundColor: primary.darkBackground,
        transition: 'all 0s',
        transitionTimingFunction: 'cubic-bezier(0, 0.25, 1)',
        borderRadius: '10rem',
        zIndex: '-1',
      },
      '&.animating': {
        color: '#fff',
        '&:before': {
          transition: 'all 1s',
          width: '100%',
        },
      },
    },
    modalHeader: {
      fontSize: headline2.fontSize,
      paddingBottom: 1.5 * unit,
      textTransform: 'uppercase',
    },
  }),
);

const ActionConfirmation = ({ confirmation, setConfirmation }) => {
  const { words, execute, onCancel = of(1) } = confirmation;
  const classes = useStyles();
  const locale = React.useContext(LocaleContext);
  const closeConfirmation = () => setConfirmation(() => null);
  const [disableButton, setDisableButton] = React.useState(false);
  return (
    <div>
      {words.header && <h3 className={classes.modalHeader}>{words.header}</h3>}
      <div style={{ paddingBottom: '30px' }}>{words.title}</div>
      {words.body && words.body}
      <Button
        variant={PRIMARY}
        className={
          disableButton
            ? `btn-primary ${classes.animatingButton} animating`
            : `btn-primary ${classes.animatingButton}`
        }
        style={{ marginRight: '15px' }}
        data-qa="proceed"
        disabled={disableButton}
        type="button"
        onClick={() => {
          setDisableButton(true);
          execute.subscribe(closeConfirmation, closeConfirmation);
        }}
      >
        {disableButton ? words.presentTenseAction || words.action : words.action}
      </Button>
      <Button
        variant={SECONDARY}
        data-qa="cancel"
        type="button"
        onClick={() => {
          onCancel.subscribe(closeConfirmation);
        }}
      >
        {words.cancel || locale['common.cancel']}
      </Button>
    </div>
  );
};

ActionConfirmation.propTypes = {
  confirmation: PropTypes.shape({
    // execute is an observable we subscribe to when user confirms action
    execute: PropTypes.shape({
      subscribe: PropTypes.func.isRequired,
    }).isRequired,
    onCancel: PropTypes.shape({
      subscribe: PropTypes.func.isRequired,
    }),
    words: PropTypes.shape({
      body: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]),
      header: PropTypes.string,
      title: PropTypes.string,
      action: PropTypes.string,
      presentTenseAction: PropTypes.string,
      cancel: PropTypes.string,
    }),
  }).isRequired,
  setConfirmation: PropTypes.func.isRequired,
};

export default ActionConfirmation;
